import React, { useCallback, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { StrapiApiFormPostAttributes } from '../../lib/integrations/strapi/types.js';
import FormButton from './FormButton';
import FormCheckbox from './FormCheckbox';
import FormInput from './FormInput';
import FormTextArea from './FormTextArea';

/**
 * Inlined at build time from NEXT_PUBLIC_RECAPTCHA_SITE_KEY. When it is absent the widget cannot
 * render, so the form must not be gated behind a token that can never arrive.
 */
const RecaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

export enum ContactUsMode {
  Connect = 'Connect',
  FreeTrial = 'Free Trial',
  DemoRequest = 'Demo Request',
}

type SubmitFormData = {
  name: string;
  company: string;
  description: string;
  email: string;
  domain: string;
  newsletterCheckbox: boolean;
};

type ContactFormProps = {
  asteriskClassName?: string;
  formTitle: string;
  formDescription: string;
  submitButtonColor: 'green' | 'blue';
  submitButtonText: string;
  mode: ContactUsMode;
  errors?: Partial<StrapiApiFormPostAttributes>;
  onSubmit: (data: SubmitFormData) => void;
};

const ContactForm: React.FC<ContactFormProps> = React.memo((props) => {
  const { asteriskClassName, formTitle, formDescription, submitButtonColor, mode, submitButtonText, errors, onSubmit } = props;

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  /**
   * An unregistered domain, an invalid key or a blocked script leaves the widget unable to ever
   * issue a token. Treating that as "no captcha available" keeps the form submittable instead of
   * silently dead, which is the failure this whole gate previously caused.
   */
  const [isRecaptchaUnavailable, setIsRecaptchaUnavailable] = useState<boolean>(false);
  const isRecaptchaRequired = Boolean(RecaptchaSiteKey) && !isRecaptchaUnavailable;
  const canSubmit = !isRecaptchaRequired || Boolean(recaptchaToken);

  const handleFormSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canSubmit) {
        return;
      }
      const data = {
        name: event.target['name-input'].value,
        company: event.target['company-input'].value,
        email: event.target['email-input'].value,
        newsletterCheckbox: event.target['newsletter-checkbox-input'].checked,
        description: event.target['description-input']?.value,
        phoneNumber: event.target['phone-input']?.value,
        jobTitle: event.target['jobtitle-input']?.value,
        domain: event.target['domain-input']?.value,
      };

      onSubmit(data);
    },
    [onSubmit, canSubmit],
  );

  const nameInput = (
    <FormInput
      asteriskClassName={asteriskClassName}
      errorMessage={errors?.full_name}
      required={true}
      type="text"
      id="name-input"
      label="Full Name"
      placeholder="Type here..."
    />
  );
  const companyInput = (
    <FormInput
      asteriskClassName={asteriskClassName}
      errorMessage={errors?.company}
      required={true}
      type="text"
      id="company-input"
      label="Company"
      placeholder="Type here..."
    />
  );
  const emailInput = (
    <FormInput
      asteriskClassName={asteriskClassName}
      errorMessage={errors?.email}
      required={true}
      type="email"
      id="email-input"
      label="Email"
      placeholder="Type here..."
    />
  );
  const descriptionInput = (
    <FormTextArea
      asteriskClassName={asteriskClassName}
      errorMessage={errors?.description}
      required={true}
      id="description-input"
      label="Description"
      placeholder="Type here..."
    />
  );
  const jobTitleInput = (
    <FormInput
      asteriskClassName={asteriskClassName}
      errorMessage={errors?.job_title}
      required={true}
      type="string"
      id="jobtitle-input"
      label="Job Title"
      placeholder="Type here..."
    />
  );
  const phoneInput = (
    <FormInput
      asteriskClassName={asteriskClassName}
      errorMessage={errors?.phone_number || errors?.phone}
      required={true}
      type="tel"
      id="phone-input"
      label="Phone"
      placeholder="Type here..."
    />
  );
  const domainInput = (
    <FormInput
      asteriskClassName={asteriskClassName}
      errorMessage={errors?.domain}
      required={true}
      type="string"
      id="domain-input"
      label="Workspace Name"
      tooltip="Enter a username for your company's instance using lowercase letter/numbers"
      placeholder="Type here..."
    />
  );

  return (
    <div className="text-center flex flex-col">
      <h2 className="text-6xl pt-6 pb-3">{formTitle}</h2>
      <p className="pb-4">{formDescription} *By submitting, you agree to our Privacy Policy.</p>
      <form onSubmit={handleFormSubmit} className="grid grid-cols-12 gap-4 w-full max-w-4xl self-center text-left">
        {mode === ContactUsMode.Connect && (
          <>
            <div className="col-span-12">{nameInput}</div>
            <div className="col-span-12 md:col-span-6">{companyInput}</div>
            <div className="col-span-12 md:col-span-6">{emailInput}</div>
            <div className="col-span-12">{descriptionInput}</div>
          </>
        )}
        {mode === ContactUsMode.DemoRequest && (
          <>
            <div className="col-span-12">{nameInput}</div>
            <div className="col-span-12 md:col-span-6">{companyInput}</div>
            <div className="col-span-12 md:col-span-6">{emailInput}</div>
            <div className="col-span-12 md:col-span-6">{jobTitleInput}</div>
            <div className="col-span-12 md:col-span-6">{phoneInput}</div>
          </>
        )}
        {mode === ContactUsMode.FreeTrial && (
          <>
            <div className="col-span-12 md:col-span-6">{nameInput}</div>
            <div className="col-span-12 md:col-span-6">{phoneInput}</div>
            <div className="col-span-12 md:col-span-6">{emailInput}</div>
            <div className="col-span-12 md:col-span-6">{companyInput}</div>
            <div className="col-span-12 md:col-span-6">{jobTitleInput}</div>
            <div className="col-span-12 md:col-span-6">{domainInput}</div>
          </>
        )}
        <div className="col-span-6 text-center">
          <FormCheckbox
            id="newsletter-checkbox-input"
            label="By checking this box, I agree to receive company news and updates. Learn more in the Privacy Policy."
          />
        </div>
        <div className="col-span-6 text-center justify-end flex">
          {RecaptchaSiteKey && (
            <ReCAPTCHA
              sitekey={RecaptchaSiteKey}
              onChange={(value) => setRecaptchaToken(value)}
              onExpired={() => setRecaptchaToken(null)}
              onErrored={() => setIsRecaptchaUnavailable(true)}
            />
          )}
        </div>
        <div className="col-span-12 flex justify-center">
          <FormButton disabled={!canSubmit} color={submitButtonColor}>
            {submitButtonText}
          </FormButton>
        </div>
      </form>
    </div>
  );
});
ContactForm.displayName = 'ContactForm';
export default ContactForm;
